import { useCallback, useEffect, useState } from 'react'
import { PainelBaixa } from '../componentes/PainelBaixa'
import { api, ErroApi } from '../lib/api'
import { useSessao } from '../lib/sessao'
import { Caixa } from './Caixa'
import './financeiro.css'

/**
 * O dia, em dinheiro.
 *
 * A tela existe para responder três perguntas, nessa ordem:
 *   1. entrou quanto hoje?
 *   2. desse quanto já caiu na conta?
 *   3. o que está atrasado?
 *
 * As duas primeiras são números DIFERENTES e a tela mostra os dois
 * lado a lado de propósito. Cartão em D+30 aparece em "recebido" e
 * não em "na conta" — quem soma os dois acha que tem caixa que não
 * tem, e paga comissão de dinheiro que ainda não chegou.
 */

type Resumo = {
  de: string; ate: string
  recebido_centavos: string
  liquidado_centavos: string
  competencia_centavos: string
  abertos: string; aberto_centavos: string
  vencidos: string; vencido_centavos: string
  por_meio: Array<{ meio: string; n: string; centavos: string }>
}

type Recebivel = {
  id: string; descricao: string; bruto_centavos: string; pago_centavos: string
  competencia: string; vencimento: string | null; status: string
  /* A rota já devolvia `pago_em`; o tipo local é que não declarava —
     e por isso a coluna "Pago em" da lista de recebidos não compilava. */
  pago_em: string | null
  meio: string | null; paciente: string | null; paciente_id: string | null
}

const brl = (c: string | number) =>
  (Number(c) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
/* Uma coluna `date` do Postgres volta serializada como timestamp cheio
   (`2026-08-12T00:00:00.000Z`). O código antigo colava `T12:00:00` no
   fim disso e virava `Invalid Date` na tela. E converter com `new Date`
   erra o dia por fuso (meia-noite UTC vira véspera no -03). A saída é
   tratar a data como TEXTO: pega os 10 primeiros e formata sem fuso. */
const data = (iso: string | null) => {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return d && m && y ? `${d}/${m}/${y}` : '—'
}
const hoje = () => new Date().toISOString().slice(0, 10)

const MEIO: Record<string, string> = {
  PIX: 'Pix', DINHEIRO: 'Dinheiro', CARTAO_CREDITO: 'Cartão de crédito',
  CARTAO_DEBITO: 'Cartão de débito', BOLETO: 'Boleto',
  TRANSFERENCIA: 'Transferência', CREDITO_PACIENTE: 'Crédito do paciente',
  CORTESIA: 'Cortesia',
}

export function Financeiro() {
  const { pode } = useSessao()
  const [de, setDe] = useState(hoje)
  const [ate, setAte] = useState(hoje)
  const [resumo, setResumo] = useState<Resumo | null>(null)
  const [pendentes, setPendentes] = useState<Recebivel[]>([])
  /* Os pagos do período. A tela só mostrava o que estava EM ABERTO, e
     por isso não havia onde estornar — a rota `/estorno` existia e era
     inalcançável. `Caixa.tsx` até dizia "ajuste só por estorno" sem
     oferecer o botão em lugar nenhum. */
  const [pagos, setPagos] = useState<Recebivel[]>([])
  const [estornando, setEstornando] = useState<Recebivel | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const carregar = useCallback(async () => {
    setErro(null)
    try {
      const [r, p, g, l] = await Promise.all([
        api<Resumo>(`/v1/financeiro/resumo?de=${de}&ate=${ate}`),
        api<{ itens: Recebivel[] }>('/v1/recebiveis?status=ABERTO&limite=100'),
        /* "Recebido" são DOIS status, e confundir os dois esvazia a
           lista: Pix, dinheiro e cortesia compensam na hora e viram
           LIQUIDADO; cartão e boleto ficam em PAGO até cair. Consultar
           só `status=PAGO` perderia justamente o que a clínica mais
           recebe. O filtro da rota aceita um valor por vez, então são
           duas chamadas. */
        api<{ itens: Recebivel[] }>(`/v1/recebiveis?status=PAGO&de=${de}&ate=${ate}&limite=100`)
          .catch(() => ({ itens: [] as Recebivel[] })),
        api<{ itens: Recebivel[] }>(`/v1/recebiveis?status=LIQUIDADO&de=${de}&ate=${ate}&limite=100`)
          .catch(() => ({ itens: [] as Recebivel[] })),
      ])
      setResumo(r); setPendentes(p.itens)
      setPagos([...g.itens, ...l.itens]
        .sort((a, b) => (b.pago_em ?? '').localeCompare(a.pago_em ?? '')))
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [de, ate])
  useEffect(() => { void carregar() }, [carregar])

  const [baixando, setBaixando] = useState<Recebivel | null>(null)

  async function baixar(r: Recebivel, meio: string) {
    try {
      await api(`/v1/recebiveis/${r.id}/baixa`, { metodo: 'POST', corpo: { meio } })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível dar baixa.')
    }
  }

  const emTransito = resumo
    ? Number(resumo.recebido_centavos) - Number(resumo.liquidado_centavos)
    : 0

  return (
    <div className="fin">
      <header className="fin__topo">
        <h1>Financeiro</h1>
        <div className="fin__periodo">
          <label htmlFor="fin-de" className="oculto-visual">De</label>
          <input id="fin-de" className="entrada" type="date"
                 value={de} onChange={(e) => setDe(e.target.value)} />
          <span className="ajuda">até</span>
          <label htmlFor="fin-ate" className="oculto-visual">Até</label>
          <input id="fin-ate" className="entrada" type="date"
                 value={ate} onChange={(e) => setAte(e.target.value)} />
          <button className="botao botao--fantasma botao--pequeno"
                  onClick={() => { setDe(hoje()); setAte(hoje()) }}>Hoje</button>
        </div>
      </header>

      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

      {carregando ? (
        <div className="esqueleto" style={{ height: 110 }} />
      ) : resumo && (
        <>
          <div className="fin__cartoes">
            <div className="fin__cartao">
              <span className="fin__rotulo">Recebido no período</span>
              <strong className="fin__valor">{brl(resumo.recebido_centavos)}</strong>
              <span className="ajuda">o paciente pagou</span>
            </div>

            <div className="fin__cartao">
              <span className="fin__rotulo">Caiu na conta</span>
              <strong className="fin__valor fin__valor--ok">
                {brl(resumo.liquidado_centavos)}
              </strong>
              <span className="ajuda">dinheiro disponível de fato</span>
            </div>

            {/* Esta caixa é a razão de as duas anteriores existirem
                separadas. Sem ela, ninguém entende a diferença. */}
            {emTransito > 0 && (
              <div className="fin__cartao fin__cartao--transito">
                <span className="fin__rotulo">Em trânsito</span>
                <strong className="fin__valor">{brl(emTransito)}</strong>
                <span className="ajuda">cobrado, ainda não compensou</span>
              </div>
            )}

            <div className="fin__cartao">
              <span className="fin__rotulo">Em aberto</span>
              <strong className={`fin__valor ${
                Number(resumo.vencido_centavos) > 0 ? 'fin__valor--erro' : ''}`}>
                {brl(resumo.aberto_centavos)}
              </strong>
              <span className="ajuda">
                {resumo.abertos} lançamento(s)
                {Number(resumo.vencidos) > 0 &&
                  ` · ${resumo.vencidos} vencido(s), ${brl(resumo.vencido_centavos)}`}
              </span>
            </div>
          </div>

          {resumo.por_meio.length > 0 && (
            <section className="cartao fin__secao">
              <h2>Por forma de pagamento</h2>
              <ul className="fin__meios">
                {resumo.por_meio.map((m) => (
                  <li key={m.meio}>
                    <span>{MEIO[m.meio] ?? m.meio}</span>
                    <span className="ajuda">{m.n}×</span>
                    <strong className="mono">{brl(m.centavos)}</strong>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      <section className="cartao fin__secao">
        <h2>Em aberto</h2>
        {pendentes.length === 0 ? (
          <p className="ajuda">Nada em aberto. </p>
        ) : (
          <div className="rolagem-x">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Paciente</th><th>Descrição</th><th>Vencimento</th>
                  <th>Valor</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pendentes.map((r) => {
                  const venceu = r.vencimento && r.vencimento < hoje()
                  return (
                    <tr key={r.id} className={venceu ? 'is-vencido' : ''}>
                      <td>{r.paciente ?? '—'}</td>
                      <td>{r.descricao}</td>
                      <td className="mono">
                        {data(r.vencimento)}
                        {venceu && <span className="selo selo--erro">vencido</span>}
                      </td>
                      <td className="mono">{brl(r.bruto_centavos)}</td>
                      <td>
                        {pode('financeiro:write') && (
                          <button className="botao botao--pequeno"
                                  onClick={() => setBaixando(r)}>Dar baixa</button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="cartao fin__secao">
        <h2>Recebidos no período</h2>
        {pagos.length === 0 ? (
          <p className="ajuda">Nenhum recebimento neste período.</p>
        ) : (
          <div className="rolagem-x">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Paciente</th><th>Descrição</th><th>Pago em</th>
                  <th>Meio</th><th>Valor</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pagos.map((r) => (
                  <tr key={r.id}>
                    <td>{r.paciente ?? '—'}</td>
                    <td>{r.descricao}</td>
                    <td className="mono">{data(r.pago_em)}</td>
                    <td>{r.meio ?? '—'}</td>
                    <td className="mono">{brl(r.pago_centavos)}</td>
                    <td>
                      {/* Estorno é DESTRUTIVO e por isso é contorno, nunca
                          preenchido: presente, não convidativo. E exige
                          motivo — o lançamento não some, ele ganha um
                          contrário, que é como partida dobrada corrige. */}
                      {pode('financeiro:write') && (
                        <button className="botao botao--fantasma botao--pequeno fin__estornar"
                                onClick={() => setEstornando(r)}>Estornar</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {estornando && (
        <Estornar r={estornando}
                  aoFechar={() => setEstornando(null)}
                  aoEstornar={async () => { setEstornando(null); await carregar() }} />
      )}

      {/* Caixa e repasse vêm depois da lista do dia: a recepção abre esta
          tela para lançar e conferir o movimento; o fechamento e o
          repasse são o ritual do fim do turno e do fim do mês. */}
      <Caixa podeEditar={pode('financeiro:write')} />

      {baixando && (
        <PainelBaixa
          bruto={Number(baixando.bruto_centavos)}
          jaPago={Number(baixando.pago_centavos)}
          aoFechar={() => setBaixando(null)}
          aoConfirmar={async (corpo) => {
            await api(`/v1/recebiveis/${baixando.id}/baixa`, { metodo: 'POST', corpo })
            setBaixando(null)
            await carregar()
          }} />
      )}
    </div>
  )
}

/**
 * Estornar um recebimento.
 *
 * O motivo é OBRIGATÓRIO na rota (`min 3, max 300`), e com razão: um
 * estorno sem explicação é indistinguível de um erro de operação quando
 * alguém for conferir o caixa três meses depois.
 *
 * O lançamento NÃO some — ganha um contrário. É assim que partida
 * dobrada corrige, e é o que permite auditar o que aconteceu em vez de
 * só ver o resultado.
 */
function Estornar({ r, aoFechar, aoEstornar }: {
  r: Recebivel; aoFechar(): void; aoEstornar(): Promise<void>
}) {
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  const SUGESTOES = [
    'Cobrança em duplicidade',
    'Valor lançado errado',
    'Paciente desistiu do pacote',
    'Meio de pagamento trocado',
  ]

  async function estornar() {
    if (motivo.trim().length < 3) return
    setSalvando(true); setErro(null)
    try {
      await api(`/v1/recebiveis/${r.id}/estorno`, {
        metodo: 'POST', corpo: { motivo: motivo.trim() },
      })
      await aoEstornar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível estornar.')
      setSalvando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <div className="porque" role="dialog" aria-modal="true" aria-label="Estornar recebimento">
        <h3>Estornar {brl(r.pago_centavos)}?</h3>
        <p className="ajuda">
          {r.descricao}{r.paciente && ` · ${r.paciente}`}
        </p>
        <p className="ajuda">
          O lançamento não é apagado — entra um contrário, com esta explicação.
          Quem conferir o caixa depois vê os dois e entende o que houve.
        </p>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="porque__chips">
          {SUGESTOES.map((o) => (
            <button key={o} type="button"
                    className={`porque__chip${motivo === o ? ' is-ativa' : ''}`}
                    aria-pressed={motivo === o}
                    onClick={() => setMotivo(motivo === o ? '' : o)}>
              {o}
            </button>
          ))}
        </div>

        <input className="entrada" value={motivo} maxLength={300}
               aria-label="Motivo do estorno"
               placeholder="ou escreva o motivo"
               onChange={(e) => setMotivo(e.target.value)} />

        <div className="porque__acoes">
          <button className="botao botao--fantasma" onClick={aoFechar}>Voltar</button>
          <button className="botao botao--perigo-contorno"
                  disabled={motivo.trim().length < 3 || salvando}
                  onClick={() => void estornar()}>
            {salvando ? 'Estornando…' : 'Estornar'}
          </button>
        </div>
      </div>
    </>
  )
}
